import React, { ComponentPropsWithoutRef, useMemo, useState } from 'react';
import { toast } from 'react-toastify';

import classNames from 'classnames';

import { useGetSIWENonceMutation, useLoginViaTonWalletMutation } from '@query';

import { WALLET_NETWORK } from '@api';

import { Modal, ModalCloseButton, ModalHeading, useModalController } from '@uikit/components/Modal';
import ModalContent from '@uikit/components/Modal/ModalContent';
import Spinner from '@uikit/components/Spinner/Spinner';
import { TonBrandColor } from '@uikit/icons/TonBrandColor';

import { useAuth } from '@shared/common/providers/AuthProvider';
import { useAuthModal } from '@shared/common/providers/GlobalModalsProvider';
import displayErrorToast from '@shared/helpers/displayErrorToast';
import { useRegisterQueryParams } from '@shared/hooks/useRegisterQueryParams';

import { useTonConnectUI, Wallet } from '@tonconnect/ui-react';
import { getErrorMessage } from '@utilities/errors/getErrorMessage';

interface Props extends ComponentPropsWithoutRef<'button'> {
  showDisconnectPrefix?: boolean;
  loginViaTonWalletMutation: ReturnType<typeof useLoginViaTonWalletMutation>;
}

const ConnectTonWalletButtonStore = ({
  showDisconnectPrefix,
  loginViaTonWalletMutation,
  className,
  disabled,
  ...props
}: Props) => {
  const { isAuth, user, isLoading } = useAuth();
  const [tonConnectUi] = useTonConnectUI();
  const addQueryRegister = useRegisterQueryParams();
  const { mutateAsync: getWalletNonce } = useGetSIWENonceMutation();
  const { mutateAsync: login, isPending: isLoggingInWithTonWallet } = loginViaTonWalletMutation;
  const authModal = useAuthModal();
  const modal = useModalController();
  const [confirmSignUpWallet, setConfirmSignUpWallet] = useState<Wallet>();

  const tonAddress = useMemo(() => {
    return user?.wallets?.find((wallet) => wallet.attributes.network === WALLET_NETWORK.TON)
      ?.attributes?.address;
  }, [user]);

  const closeModals = () => {
    authModal.close();

    if (modal.isOpen) {
      modal.close();
    }
  };

  const handleLogin = async ({
    wallet,
    userCreationAllowed = false,
  }: {
    wallet: Wallet;
    userCreationAllowed: boolean;
  }) => {
    try {
      await login({ ...wallet, userCreationAllowed });

      toast.success('TON wallet successfully connected');

      if (userCreationAllowed) {
        addQueryRegister({
          loginMethod: 'TONConnect',
        });
      }

      closeModals();
    } catch (e) {
      const error = e as unknown as { code?: number; message?: string };

      if (
        error?.code === 400 &&
        error?.message === 'User creation not allowed' &&
        !userCreationAllowed
      ) {
        setConfirmSignUpWallet(wallet);
        modal.open();

        return;
      }

      toast.error(`Unable to connect TON wallet. ${getErrorMessage(error)}`);
    }
  };

  const handleButtonClick = async () => {
    tonConnectUi.setConnectRequestParameters({ state: 'loading' });

    tonConnectUi.openModal();

    if (tonConnectUi.wallet) {
      await tonConnectUi.disconnect();
    }

    let nonce = '';

    try {
      nonce = await getWalletNonce();
    } catch (error) {
      toast.error(`Unable to retrieve ton proof. ${getErrorMessage(error)}`);

      tonConnectUi.closeModal();

      return;
    }

    tonConnectUi.setConnectRequestParameters({ state: 'ready', value: { tonProof: nonce } });

    const unsubscribe = tonConnectUi.onModalStateChange(async (state) => {
      try {
        unsubscribe();

        if (
          state.closeReason !== 'wallet-selected' ||
          !tonConnectUi.wallet?.connectItems?.tonProof
        ) {
          return;
        }

        await handleLogin({ wallet: tonConnectUi.wallet, userCreationAllowed: false });
      } catch (error) {
        displayErrorToast({ error });
      }
    });
  };

  const isConnectWalletButtonDisabled = isLoading || isLoggingInWithTonWallet || disabled;

  if (tonAddress || isAuth) {
    return null;
  }

  return (
    <>
      <button
        disabled={isConnectWalletButtonDisabled}
        onClick={handleButtonClick}
        data-qa="connect-wallet-button"
        className={classNames(
          'relative button button-solid-neutral-600 button-lg rounded-lg px-2 flex-1 w-full gap-2',
          className,
        )}
        {...props}
      >
        <TonBrandColor
          className={classNames('icon max-sm:absolute left-2', {
            'grayscale opacity-20': disabled,
          })}
        />
        TON Connect
      </button>
      <Modal {...modal.props}>
        <ModalContent className="flex w-full max-w-[520px] text-center flex-col p-6 sm:px-12 border-0">
          <ModalCloseButton className="self-end" />

          <ModalHeading className="mb-6">Confirm Sign Up</ModalHeading>

          <p className="text-center font-semibold">
            Account with this address was not found. Would you like to create a new Magic ID
            Account?
          </p>

          <div className="flex gap-3">
            <button
              className={classNames(
                'button button-outline-primary button-lg text-primary flex-1 gap-2 mt-8 transition',
              )}
              onClick={closeModals}
            >
              Cancel
            </button>

            <button
              disabled={!confirmSignUpWallet || isLoading}
              className={classNames(
                'button button-primary button-lg text-neutral-50 flex-1 gap-2 mt-8 transition flex',
              )}
              onClick={() =>
                confirmSignUpWallet
                  ? handleLogin({ wallet: confirmSignUpWallet, userCreationAllowed: true })
                  : closeModals()
              }
            >
              {isLoading && <Spinner size="sm" color="light" />}
              Sign Up
            </button>
          </div>
        </ModalContent>
      </Modal>
    </>
  );
};

export default ConnectTonWalletButtonStore;
